type FetchOptions = Omit<RequestInit, "headers"> & {
  headers?: Record<string, string>;
  json?: unknown;
};

type UploadOptions = {
  url: string;
  file: File;
  timeoutMs?: number;
  signal?: AbortSignal;
  onProgress?: (percent: number) => void;
};

export class HttpError extends Error {
  constructor(public response: Response) {
    super(`HTTP ${response.status} ${response.statusText}`);
  }
}

export const http = {
  async fetch(
    url: string,
    { json, headers, ...rest }: FetchOptions = {},
  ): Promise<Response> {
    const response = await fetch(url, {
      ...rest,
      headers:
        json !== undefined
          ? { "Content-Type": "application/json", ...headers }
          : headers,
      ...(json !== undefined && { body: JSON.stringify(json) }),
    });
    if (!response.ok) throw new HttpError(response);
    return response;
  },

  async json<T = unknown>(url: string, options?: FetchOptions): Promise<T> {
    return (await http.fetch(url, options)).json();
  },

  async blob(url: string, options?: FetchOptions): Promise<Blob> {
    return (await http.fetch(url, options)).blob();
  },

  // XHR, not fetch: fetch has no upload-progress events.
  upload({
    url,
    file,
    timeoutMs,
    signal,
    onProgress,
  }: UploadOptions): Promise<void> {
    return new Promise((resolve, reject) => {
      if (signal?.aborted) {
        reject(new Error("Upload aborted"));
        return;
      }
      const xhr = new XMLHttpRequest();
      xhr.open("PUT", url);
      if (file.type) xhr.setRequestHeader("Content-Type", file.type);
      if (timeoutMs) xhr.timeout = timeoutMs;
      if (onProgress) {
        xhr.upload.onprogress = (event) => {
          if (event.lengthComputable && event.total > 0) {
            onProgress(Math.round((event.loaded / event.total) * 100));
          }
        };
      }
      xhr.onload = () => {
        if (xhr.status >= 200 && xhr.status < 300) resolve();
        else reject(new Error(`HTTP ${xhr.status}`));
      };
      xhr.onerror = () => reject(new Error("Network error"));
      xhr.ontimeout = () => reject(new Error("Upload timed out"));
      xhr.onabort = () => reject(new Error("Upload aborted"));
      signal?.addEventListener("abort", () => xhr.abort(), { once: true });
      xhr.send(file);
    });
  },
};
